import { useState, useEffect, useRef, useCallback } from "react";
import ScriptText from "@/components/ui/ScriptText";

interface DiscoveryImage {
  image: string;
  alt: string;
}

interface DiscoverySection {
  scriptAccent?: string;
  heading: string;
  body: string;
  images: DiscoveryImage[];
}

interface RetreatDiscoveryProps {
  scriptAccent?: string;
  heading?: string;
  intro?: string;
  sections: DiscoverySection[];
  resolveImage: (publicPath: string, value: string) => string;
}

/** Auto-rotating image stack with crossfade */
function RotatingImage({
  images,
  resolveImage,
  interval = 5000,
}: {
  images: DiscoveryImage[];
  resolveImage: (publicPath: string, value: string) => string;
  interval?: number;
}) {
  const [activeIndex, setActiveIndex] = useState(0);
  const timerRef = useRef<ReturnType<typeof setInterval>>(undefined);

  useEffect(() => {
    if (images.length <= 1) return;

    timerRef.current = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % images.length);
    }, interval);

    return () => clearInterval(timerRef.current);
  }, [images.length, interval]);

  const advance = useCallback(() => {
    setActiveIndex((prev) => (prev + 1) % images.length);
  }, [images.length]);

  return (
    <div
      className="relative aspect-[4/5] w-full overflow-hidden rounded-sm"
      onClick={advance}
    >
      {images.map((img, i) => (
        <img
          key={i}
          src={resolveImage("/images/retreats/", img.image)}
          alt={img.alt}
          className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-1000 ease-in-out ${
            i === activeIndex ? "opacity-100" : "opacity-0"
          }`}
          loading="lazy"
          width={800}
          height={1000}
        />
      ))}

      {/* Subtle progress dots */}
      {images.length > 1 && (
        <div className="absolute bottom-4 left-1/2 z-10 flex -translate-x-1/2 gap-2">
          {images.map((_, i) => (
            <button
              key={i}
              type="button"
              onClick={(e) => { e.stopPropagation(); setActiveIndex(i); }}
              className={`h-1.5 rounded-full transition-all duration-500 ${
                i === activeIndex
                  ? "w-5 bg-parchment/80"
                  : "w-1.5 bg-parchment/40"
              }`}
              aria-label={`View image ${i + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default function RetreatDiscovery({
  scriptAccent = "Discover the Region",
  heading,
  intro,
  sections,
  resolveImage,
}: RetreatDiscoveryProps) {
  if (!sections.length) return null;

  return (
    <section className="bg-parchment px-6 py-24 lg:px-12 lg:py-32">
      <div className="mx-auto max-w-6xl">
        {/* Section intro */}
        <div className="reveal mb-6 text-center">
          <ScriptText className="block text-2xl lg:text-3xl">
            {scriptAccent}
          </ScriptText>
        </div>
        {heading && (
          <h2 className="reveal mb-6 text-center font-serif font-light text-soft-charcoal">
            {heading}
          </h2>
        )}
        {intro && (
          <p className="reveal mx-auto mb-20 max-w-2xl text-center font-body leading-relaxed text-soft-charcoal/70 lg:text-lg">
            {intro}
          </p>
        )}

        {/* Alternating blocks */}
        <div className="space-y-24 lg:space-y-32">
          {sections.map((section, i) => {
            const imageOnRight = i % 2 === 0;

            return (
              <div
                key={i}
                className="reveal grid items-center gap-12 lg:grid-cols-2 lg:gap-20"
              >
                {/* Text column */}
                <div
                  className={`${imageOnRight ? "lg:order-1" : "lg:order-2"}`}
                >
                  {section.scriptAccent && (
                    <ScriptText className="mb-2 block text-xl text-faded-terracotta/80 lg:text-2xl">
                      {section.scriptAccent}
                    </ScriptText>
                  )}
                  <h3 className="mb-6 font-serif text-2xl font-light tracking-wide text-soft-charcoal lg:text-3xl">
                    {section.heading}
                  </h3>
                  <div className="space-y-5">
                    {section.body.split("\n\n").map((paragraph, j) => (
                      <p
                        key={j}
                        className="font-body leading-[1.9] text-soft-charcoal/70 lg:text-[1.05rem]"
                      >
                        {paragraph}
                      </p>
                    ))}
                  </div>

                  {/* Decorative line */}
                  <span className="mt-8 block h-px w-16 bg-muted-gold/30" />
                </div>

                {/* Image column — auto-rotating */}
                <div
                  className={`${imageOnRight ? "lg:order-2" : "lg:order-1"}`}
                >
                  {section.images.length > 0 ? (
                    <RotatingImage
                      images={section.images}
                      resolveImage={resolveImage}
                      interval={5000 + i * 500} /* stagger intervals so they don't all flip at once */
                    />
                  ) : (
                    <div className="aspect-[4/5] w-full rounded-sm bg-warm-stone/10" />
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
